import { useSubscription } from '@apollo/client';
import { setUser } from '@sentry/react';
import { addHours, isFuture } from 'date-fns';
import type { ReactNode } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';

import {
  Cryptocurrency,
  Currency,
  DeviceWasUpdatedEvent,
  EnabledWallet,
  Sport,
} from '__generated__/globalTypes';
import { createLink } from 'atoms/typography/Link';
import { SETTINGS_SECURITY } from 'constants/__generated__/routes';
import { useGraphqlContext } from 'contexts/graphql';
import { useSportContext } from 'contexts/sport';
import { idFromObject } from 'gql/idFromObject';
import { useIsAuthorizedRouteForSecondFactorRecommendation } from 'hooks/isAuthorizedRouteForSecondFactorRecommendation';
import { useFeatureFlags } from 'hooks/useFeatureFlags';
import { parseMobileApp } from 'hooks/useIsMobileApp';
import { getBlockchainCardsCount, getRarities } from 'lib/cardCounts';
import { isType } from 'lib/gql';
import { asObject } from 'lib/json';
import {
  importantNotificationCategories,
  notificationCategories,
} from 'lib/notifications';
import type { MobileAppVersion } from 'lib/storage/sessionStorage';

import { VERSION, isProduction } from '../../config';
import { useConfigContext } from '../config';
import { useDeviceFingerprintContext } from '../deviceFingerprint';
import { useEventsContext } from '../events';
import { useSnackNotificationContext } from '../snackNotification';
import type {
  onDeviceWasUpdated,
  onDeviceWasUpdatedVariables,
} from './__generated__/queries.graphql';
import { CurrentUserContext } from './index';
import { onDeviceSubscription, subscription } from './queries';

type Props = {
  children: ReactNode;
};

const getMobileAppValue = (mobileApp: string | null) => {
  const { isAndroidApp, isGoldApp, isIosApp } = parseMobileApp(
    mobileApp as MobileAppVersion | null
  );
  if (isAndroidApp) {
    return 'android';
  }
  if (isGoldApp) {
    return 'ios-gold';
  }
  if (isIosApp) {
    return 'ios';
  }
  return 'native-web';
};
/**
 * Provides the current logged in user.
 */
export const CurrentUserProvider = ({ children }: Props) => {
  const { setApiKey } = useGraphqlContext();
  const { identify: identifyAnalytics } = useEventsContext();
  const { currentUser, refetch, defaultFiatCurrency, loading } =
    useConfigContext();
  const [searchParams] = useSearchParams();
  const { sport } = useSportContext();
  const { showNotification } = useSnackNotificationContext();
  const [currentDeviceFingerPrint, setCurrentDeviceFingerPrint] =
    useState<string>();
  const [shouldResubscribe, setShouldResubscribe] = useState(false);
  const { monitoredFlags, identify: identifyFeatureFlagsUser } =
    useFeatureFlags();
  const { deviceFingerprint } = useDeviceFingerprintContext();
  const isAuthorizedRouteForSecondFactorRecommendation =
    useIsAuthorizedRouteForSecondFactorRecommendation();
  const [
    hasShownSecondFactorRecommendation,
    setHasShownSecondFactorRecommendation,
  ] = useState(false);

  useEffect(() => {
    deviceFingerprint()
      .then(v => setCurrentDeviceFingerPrint(v))
      .then(() => setShouldResubscribe(true));
  }, [deviceFingerprint]);

  useSubscription(subscription, {
    skip: !currentUser,
    shouldResubscribe,
    variables: {
      sport,
      notificationCategories,
      importantNotificationCategories,
    },
    onComplete: () => {
      setShouldResubscribe(false);
    },
    context: {
      headers: {
        DEVICE_FINGERPRINT: currentDeviceFingerPrint,
      },
    },
  });

  const currentDeviceConfirmed = currentUser?.confirmedDevice;
  const currentDeviceId = currentUser?.currentDevice?.id;
  useSubscription<onDeviceWasUpdated, onDeviceWasUpdatedVariables>(
    onDeviceSubscription,
    {
      skip: !currentDeviceId || !!currentDeviceConfirmed,
      onData: ({ data: { data } }) => {
        if (data) {
          const { deviceWasUpdated } = data;
          if (
            deviceWasUpdated &&
            deviceWasUpdated.eventType === DeviceWasUpdatedEvent.confirmed &&
            deviceWasUpdated.id === currentDeviceId
          ) {
            showNotification('deviceSuccessfullyConfirmed');
            refetch();
          }
        }
      },
    }
  );

  const blockchainCardsCount = getBlockchainCardsCount(currentUser?.cardCounts);

  const mobileApp = getMobileAppValue(searchParams.get('mobileApp'));
  useEffect(() => {
    if (currentUser?.id && identifyFeatureFlagsUser) {
      identifyFeatureFlagsUser({
        key: currentUser.id,
        custom: {
          ...asObject(currentUser.featureFlagCustomAttributes),
          webVersion: +VERSION,
          webviewPlatform: mobileApp,
        },
      });
    }
  }, [
    identifyFeatureFlagsUser,
    currentUser?.id,
    currentUser?.featureFlagCustomAttributes,
    mobileApp,
  ]);

  useEffect(() => {
    if (setUser) {
      setUser(
        currentUser
          ? {
              id: idFromObject(currentUser?.id),
              username: currentUser?.slug,
            }
          : null
      );
    }
  }, [currentUser]);

  useEffect(() => {
    if (currentUser?.apiKey) {
      setApiKey(currentUser?.apiKey);
    }
  }, [currentUser?.apiKey, setApiKey]);

  useEffect(() => {
    if (currentUser?.id) {
      identifyAnalytics(idFromObject(currentUser.id)!, {
        created: currentUser?.createdAt,
        feature_flags: monitoredFlags,
        ...(sport === Sport.BASEBALL && {
          mlbOnboarded: currentUser?.anyUserSportProfile?.onboarded,
        }),
      });
    }
  }, [
    currentUser?.id,
    currentUser?.createdAt,
    identifyAnalytics,
    monitoredFlags,
    currentUser?.anyUserSportProfile?.onboarded,
    sport,
  ]);

  const availableBalanceForWithdrawalPositive = currentUser
    ? currentUser.availableBalanceForWithdrawal > 0n
    : false;

  useEffect(() => {
    const isNewUser =
      currentUser?.createdAt && isFuture(addHours(currentUser.createdAt, 2));

    if (
      !currentUser?.otpRequiredForLogin &&
      currentUser?.confirmed &&
      (blockchainCardsCount > 0 || availableBalanceForWithdrawalPositive) &&
      // hide two factor notification for new users (we don't want to distract them during onboarding flow)
      !isNewUser &&
      isProduction
    ) {
      if (
        isAuthorizedRouteForSecondFactorRecommendation &&
        !hasShownSecondFactorRecommendation
      ) {
        setHasShownSecondFactorRecommendation(true);
        showNotification('secondFactorRecommendation', {
          link: createLink(SETTINGS_SECURITY),
        });
      }
    }
  }, [
    currentUser?.id,
    currentUser?.otpRequiredForLogin,
    currentUser?.confirmed,
    currentUser?.createdAt,
    availableBalanceForWithdrawalPositive,
    blockchainCardsCount,
    showNotification,
    isAuthorizedRouteForSecondFactorRecommendation,
    hasShownSecondFactorRecommendation,
    setHasShownSecondFactorRecommendation,
  ]);

  const fiatWalletAccountable = useMemo(() => {
    if (!currentUser) return null;
    return currentUser.myAccounts
      .map(a =>
        isType(a.accountable, 'PrivateFiatWalletAccount') ? a.accountable : null
      )
      .filter(Boolean)?.[0];
  }, [currentUser]);

  const fiatCurrency = useMemo(() => {
    if (fiatWalletAccountable?.publicInfo.currency) {
      return fiatWalletAccountable.publicInfo.currency;
    }
    return currentUser?.userSettings.fiatCurrency || defaultFiatCurrency;
  }, [
    currentUser?.userSettings.fiatCurrency,
    defaultFiatCurrency,
    fiatWalletAccountable,
  ]);

  const cryptocurrency = useMemo(() => {
    if (currentUser?.userSettings.cryptocurrency) {
      return currentUser?.userSettings.cryptocurrency;
    }
    return Cryptocurrency.WEI;
  }, [currentUser?.userSettings.cryptocurrency]);

  const enabledWallets = currentUser?.profile.enabledWallets || undefined;

  const hasMigratedAndSetupWallets = !!enabledWallets;

  const showCryptoWallet = hasMigratedAndSetupWallets
    ? enabledWallets.includes(EnabledWallet.ETH)
    : true;

  const showFiatWallet = !!enabledWallets?.includes(EnabledWallet.FIAT);

  const onlyShowFiatCurrency = showFiatWallet && !showCryptoWallet;

  const emailConfirmationNeeded = !currentUser?.confirmed;
  const currentDeviceConfirmationNeeded = !currentDeviceConfirmed;

  const currency = useMemo(() => {
    if (onlyShowFiatCurrency) return Currency.FIAT;
    return currentUser?.userSettings.currency || Currency.FIAT;
  }, [currentUser?.userSettings.currency, onlyShowFiatCurrency]);

  const displayEth = useMemo(
    () =>
      Boolean(
        currentUser?.depositedEth ||
          (currentUser?.availableBalanceForWithdrawal &&
            currentUser?.availableBalanceForWithdrawal > 0n)
      ),
    [currentUser]
  );

  const isMe = useCallback(
    (arg?: { slug?: string } | string | null) =>
      !!currentUser?.slug &&
      !!(typeof arg === 'string'
        ? arg === currentUser?.slug
        : arg?.slug === currentUser?.slug),
    [currentUser?.slug]
  );

  const rarities = useMemo(() => {
    return getRarities(currentUser?.cardCounts);
  }, [currentUser?.cardCounts]);

  const { footballLast30DaysLineupsCount = 0 } = currentUser || {};
  const onboardedFootball = footballLast30DaysLineupsCount > 0;

  const value = useMemo(
    () => ({
      currentUser,
      onboarded: {
        FOOTBALL: onboardedFootball,
        BASEBALL: false,
        NBA: false,
      },
      currency,
      fiatCurrency,
      cryptocurrency,
      fiatWalletAccountable,
      displayEth,
      refetch,
      loading,
      blockchainCardsCount,
      rarities,
      walletPreferences: {
        enabledWallets,
        showCryptoWallet,
        showFiatWallet,
        hasMigratedAndSetupWallets,
        onlyShowFiatCurrency,
      },
      emailConfirmationNeeded,
      currentDeviceConfirmationNeeded,
      isMe,
      footballUserProfile: isType(
        currentUser?.anyUserSportProfile,
        'FootballUserSportProfile'
      )
        ? currentUser?.anyUserSportProfile
        : null,
    }),
    [
      currentUser,
      onboardedFootball,
      currency,
      fiatCurrency,
      cryptocurrency,
      fiatWalletAccountable,
      displayEth,
      refetch,
      loading,
      blockchainCardsCount,
      rarities,
      enabledWallets,
      showCryptoWallet,
      showFiatWallet,
      hasMigratedAndSetupWallets,
      onlyShowFiatCurrency,
      emailConfirmationNeeded,
      currentDeviceConfirmationNeeded,
      isMe,
    ]
  );

  return (
    <CurrentUserContext.Provider value={value}>
      {children}
    </CurrentUserContext.Provider>
  );
};
